// web/js/path-breadcrumb/PathBreadcrumb.tsx
//
// Folder-path breadcrumb for a file. Renders link-rooted, department-rooted,
// or project-rooted breadcrumbs depending on whether the file is being viewed
// via a shared link, inside a department folder, or from the top-level project
// files list.
//
// Extracted from player-columns/PlayerColumn.tsx so the breadcrumb can be
// reused independently of the multi-column player tree.

import React from 'react';
import type { GetFileResponse } from '../player-loader/loadFile';

export interface PathBreadcrumbProps {
  data: GetFileResponse;
  linkId?: string | null;
  projectId: number;
}

/**
 * Folder-path breadcrumb. Renders link-rooted, department-rooted, or
 * project-rooted breadcrumbs depending on whether the view is via a shared
 * link, a department folder, or the top-level project files list.
 */
export function PathBreadcrumb({ data, linkId, projectId }: PathBreadcrumbProps) {
  const fileId = data.id;
  const items: React.ReactNode[] = [];

  if (linkId) {
    items.push(
      <a key="root" href={`files?link=${linkId}&show=${fileId}`}>
        <li>
          <img className="file-path-icon" src="img/Folder.svg" alt="" />
          {data.link?.link_name ? data.link.link_name : 'Files'}
        </li>
      </a>,
    );

    if (data.link?.link_relative_path && data.link?.link_relative_path !== '/') {
      const components = data.link.link_relative_path.split('/').filter(Boolean);
      let tempPath = '';
      components.forEach((component, i) => {
        tempPath += '/' + component;
        items.push(
            <a
                key={`p-${i}`}
                href={`files?link=${linkId}&folder=${encodeURIComponent(tempPath)}&show=${fileId}`}
            >
              <li>
                <img className="file-path-icon" src="img/Folder.svg" alt=""/>
                {component}
              </li>
            </a>,
        );
      });
    }

  } else {
    items.push(
        <a key="root" href={`files?project=${projectId}&show=${fileId}`}>
          <li>
            <img className="file-path-icon" src="img/Folder.svg" alt=""/>
            Files
          </li>
        </a>,
    );

    if (data.department_id > -1 && data.department_name) {
      items.push(
        <a
          key="dept"
          href={`files?dept=${data.department_id}&project=${projectId}&show=${fileId}`}
        >
          <li>
            <img className="file-path-icon" src="img/Dept_Folder.svg" alt="" />
            {data.department_name}
          </li>
        </a>,
      );
    }

    if (data.path && data.path !== '/') {
      const components = data.path.split('/').filter(Boolean);
      let tempPath = '';
      components.forEach((component, i) => {
        tempPath += '/' + component;
        items.push(
          <a
            key={`p-${i}`}
            href={`files?dept=${data.department_id}&folder=${encodeURIComponent(tempPath)}&project=${projectId}&show=${fileId}`}
          >
            <li>
              <img className="file-path-icon" src="img/Folder.svg" alt="" />
              {component}
            </li>
          </a>,
        );
      });
    }
  }

  return (
    <ul
      className="file-path-selector"
    >
      {items}
    </ul>
  );
}

export default PathBreadcrumb;
